/**
 * Element ID Registry — Single Source of Truth for all interactive element IDs.
 *
 * Every button, link, input, and clickable element gets a unique, descriptive ID
 * registered here. This guarantees:
 *   - Compile-time uniqueness (duplicate keys = TypeScript error)
 *   - Stable Playwright selectors (#my-element-btn)
 *   - Accessibility linking (aria-describedby, aria-labelledby, htmlFor)
 *   - Unambiguous agent/developer communication
 *
 * Naming: <page/area>-<action/role>-<context>-<suffix>
 * Suffixes: -btn, -link, -input, -toggle, -tab, -dropdown, -error, -label, -region
 *
 * Usage:
 *   import { EID } from '@/lib/element-ids.registry';
 *   <Button id={EID['dashboard-create-workspace-btn']}>Create</Button>
 */

export const EID = {
  // ─── Landing: Nav ────────────────────────────────────────────────────
  'landing-nav-home-link': 'landing-nav-home-link',
  'landing-nav-tools-link': 'landing-nav-tools-link',
  'landing-nav-pricing-link': 'landing-nav-pricing-link',
  'landing-nav-faq-link': 'landing-nav-faq-link',
  'landing-nav-language-dropdown': 'landing-nav-language-dropdown',
  'landing-nav-login-link': 'landing-nav-login-link',
  'landing-nav-register-link': 'landing-nav-register-link',

  // ─── Landing: Hero ───────────────────────────────────────────────────
  'landing-hero-register-btn': 'landing-hero-register-btn',
  'landing-hero-explore-tools-link': 'landing-hero-explore-tools-link',

  // ─── Landing: Tool Categories ────────────────────────────────────────
  'landing-tools-studio-link': 'landing-tools-studio-link',
  'landing-tools-text-link': 'landing-tools-text-link',
  'landing-tools-amazon-link': 'landing-tools-amazon-link',
  'landing-tools-utilities-link': 'landing-tools-utilities-link',

  // ─── Landing: Variants ──────────────────────────────────────────────
  // (no interactive elements — bento grid is display-only)

  // ─── Landing: Free Tools ────────────────────────────────────────────
  'landing-freetools-converter-link': 'landing-freetools-converter-link',
  'landing-freetools-bytecounter-link': 'landing-freetools-bytecounter-link',
  'landing-freetools-more-link': 'landing-freetools-more-link',

  // ─── Landing: FAQ ───────────────────────────────────────────────────
  'landing-faq-toggle-1-btn': 'landing-faq-toggle-1-btn',
  'landing-faq-toggle-2-btn': 'landing-faq-toggle-2-btn',
  'landing-faq-toggle-3-btn': 'landing-faq-toggle-3-btn',
  'landing-faq-toggle-4-btn': 'landing-faq-toggle-4-btn',
  'landing-faq-toggle-5-btn': 'landing-faq-toggle-5-btn',
  'landing-faq-answer-1-region': 'landing-faq-answer-1-region',
  'landing-faq-answer-2-region': 'landing-faq-answer-2-region',
  'landing-faq-answer-3-region': 'landing-faq-answer-3-region',
  'landing-faq-answer-4-region': 'landing-faq-answer-4-region',
  'landing-faq-answer-5-region': 'landing-faq-answer-5-region',

  // ─── Landing: CTA ───────────────────────────────────────────────────
  'landing-cta-register-btn': 'landing-cta-register-btn',

  // ─── Landing: Footer ────────────────────────────────────────────────
  'landing-footer-home-link': 'landing-footer-home-link',
  'landing-footer-tools-link': 'landing-footer-tools-link',
  'landing-footer-pricing-link': 'landing-footer-pricing-link',
  'landing-footer-register-link': 'landing-footer-register-link',
  'landing-footer-faq-link': 'landing-footer-faq-link',
  'landing-footer-login-link': 'landing-footer-login-link',
  'landing-footer-imprint-link': 'landing-footer-imprint-link',
  'landing-footer-privacy-link': 'landing-footer-privacy-link',
  'landing-footer-terms-link': 'landing-footer-terms-link',
  // ─── Product Detail: Hero ───────────────────────────────────────────
  'product-hero-sync-btn': 'product-hero-sync-btn',
  'product-hero-more-actions-btn': 'product-hero-more-actions-btn',
  'product-hero-manage-images-btn': 'product-hero-manage-images-btn',
  'product-hero-copy-sku-btn': 'product-hero-copy-sku-btn',
  'product-hero-copy-ean-btn': 'product-hero-copy-ean-btn',
  'product-hero-copy-asin-btn': 'product-hero-copy-asin-btn',
  'product-hero-copy-external-id-btn': 'product-hero-copy-external-id-btn',

  // ─── Product Detail: Readiness Banner ─────────────────────────────
  'product-readiness-start-btn': 'product-readiness-start-btn',

  // ─── Product Detail: Tabs ─────────────────────────────────────────
  'product-tab-overview': 'product-tab-overview',
  'product-tab-content': 'product-tab-content',
  'product-tab-assets': 'product-tab-assets',
  'product-tab-channels': 'product-tab-channels',
  'product-tab-quick-edit': 'product-tab-quick-edit',
  'product-tab-automation': 'product-tab-automation',
  'product-tab-history': 'product-tab-history',
  'product-tab-rawdata': 'product-tab-rawdata',

  // ─── Product Detail: Overview Tab ─────────────────────────────────
  'product-overview-edit-content-link': 'product-overview-edit-content-link',
  'product-overview-view-channels-link': 'product-overview-view-channels-link',
  'product-overview-add-channel-btn': 'product-overview-add-channel-btn',

  // ─── Dashboard ─────────────────────────────────────────────────────
  'dashboard-heading': 'dashboard-heading',
  'dashboard-header-gallery-link': 'dashboard-header-gallery-link',
  'dashboard-header-generate-btn': 'dashboard-header-generate-btn',
  'dashboard-email-warning-region': 'dashboard-email-warning-region',
  'dashboard-stats-credits-region': 'dashboard-stats-credits-region',
  'dashboard-stats-images-region': 'dashboard-stats-images-region',
  'dashboard-stats-products-region': 'dashboard-stats-products-region',

  // ─── Dashboard: Favorite Tools ─────────────────────────────────────
  'dashboard-favorites-section': 'dashboard-favorites-section',
  'dashboard-favorites-all-tools-link': 'dashboard-favorites-all-tools-link',
  'dashboard-favorites-list': 'dashboard-favorites-list',

  // ─── Dashboard: Recent Images ──────────────────────────────────────
  'dashboard-images-section': 'dashboard-images-section',
  'dashboard-images-gallery-link': 'dashboard-images-gallery-link',
  'dashboard-images-create-btn': 'dashboard-images-create-btn',
  'dashboard-images-batch-badge': 'dashboard-images-batch-badge',

  // ─── Dashboard: Quick Actions ──────────────────────────────────────
  'dashboard-quick-generate-link': 'dashboard-quick-generate-link',
  'dashboard-quick-credits-link': 'dashboard-quick-credits-link',
  'dashboard-quick-settings-link': 'dashboard-quick-settings-link',

  // ─── Dashboard: Transactions ───────────────────────────────────────
  'dashboard-transactions-section': 'dashboard-transactions-section',
  'dashboard-transactions-all-link': 'dashboard-transactions-all-link',

  // ─── Dashboard: Plan Card ──────────────────────────────────────────
  'dashboard-plan-section': 'dashboard-plan-section',
  'dashboard-plan-upgrade-link': 'dashboard-plan-upgrade-link',
  'dashboard-plan-topup-btn': 'dashboard-plan-topup-btn',

  // ─── Amazon Audit: Potential Check ─────────────────────────────────
  'audit-potential-check-region': 'audit-potential-check-region',
  'audit-potential-remake-btn': 'audit-potential-remake-btn',
  'audit-potential-view-full-btn': 'audit-potential-view-full-btn',

  // ─── Text Tools: Brand (Product Brands ai_description) ─────────────
  'text-tool-brand-dropdown': 'text-tool-brand-dropdown',
  'text-vorlage-brand-dropdown': 'text-vorlage-brand-dropdown',

  // ─── Shared: Vision Image Picker ───────────────────────────────────
  'vision-image-file-input': 'vision-image-file-input',
  'vision-image-clear-btn': 'vision-image-clear-btn',

  // ─── Text Tools: Multi-Variant + Refine ────────────────────────────
  'text-tool-variants-dropdown': 'text-tool-variants-dropdown',
  'text-tool-variant-switcher-region': 'text-tool-variant-switcher-region',
  'text-tool-refine-shorter-btn': 'text-tool-refine-shorter-btn',
  'text-tool-refine-longer-btn': 'text-tool-refine-longer-btn',
  'text-tool-refine-casual-btn': 'text-tool-refine-casual-btn',
  'text-tool-refine-formal-btn': 'text-tool-refine-formal-btn',
  'text-tool-refine-with_hashtags-btn': 'text-tool-refine-with_hashtags-btn',
  'text-tool-refine-new_hook-btn': 'text-tool-refine-new_hook-btn',

  // ─── Profile: MFA Enrollment ───────────────────────────────────────
  'mfa-qr': 'mfa-qr',
  'mfa-enroll-code': 'mfa-enroll-code',
  'mfa-enroll-confirm': 'mfa-enroll-confirm',
  'mfa-download-codes': 'mfa-download-codes',
  'mfa-recovery-codes': 'mfa-recovery-codes',

  // ─── Auth: MFA Challenge (Login 2nd factor) ────────────────────────
  'mfa-challenge-code': 'mfa-challenge-code',
  'mfa-challenge-verify': 'mfa-challenge-verify',
  'mfa-use-recovery': 'mfa-use-recovery',

  // ─── Admin: MFA Enforcement Banner ─────────────────────────────────
  'mfa-enforcement-banner': 'mfa-enforcement-banner',
  'mfa-banner-setup': 'mfa-banner-setup',

  // ─── Templates: Page (A — Refined) ─────────────────────────────────
  'templates-marketplace-link': 'templates-marketplace-link',
  'templates-new-template-btn': 'templates-new-template-btn',
  'templates-creator-pill-link': 'templates-creator-pill-link',
  'templates-info-strip-toggle': 'templates-info-strip-toggle',
  'templates-tab-templates-tab': 'templates-tab-templates-tab',
  'templates-tab-collections-tab': 'templates-tab-collections-tab',
  'templates-search-input': 'templates-search-input',
  'templates-type-all-btn': 'templates-type-all-btn',
  'templates-type-image-btn': 'templates-type-image-btn',
  'templates-type-text-btn': 'templates-type-text-btn',
  'templates-status-dropdown': 'templates-status-dropdown',
  'templates-view-list-btn': 'templates-view-list-btn',
  'templates-view-grid-btn': 'templates-view-grid-btn',
  'templates-sort-dropdown': 'templates-sort-dropdown',
  'templates-new-card-btn': 'templates-new-card-btn',
} as const satisfies Record<string, string>;

export type ElementId = keyof typeof EID;
